import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Snackbar,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import ComputerIcon from '@mui/icons-material/Computer';
import SpeedIcon from '@mui/icons-material/Speed';
import PolicyIcon from '@mui/icons-material/Policy';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ChatIcon from '@mui/icons-material/Chat';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import TimelineIcon from '@mui/icons-material/Timeline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GetAppIcon from '@mui/icons-material/GetApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import nistService from '../services/nistService';

function MarketValidation() {
  const navigate = useNavigate();
  const [copySuccess, setCopySuccess] = useState(false);
  const [mvpRefreshing, setMvpRefreshing] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleRefreshMvp = async () => {
    setMvpRefreshing(true);
    try {
      // Refresh logic if needed
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (err) {
      console.error('Error refreshing MVP data:', err);
    } finally {
      setMvpRefreshing(false);
    }
  };

  const handleCopyMvpInfo = async () => {
    const mvpContent = `# NSCAT Market Validation Report (Revised)
## Network Security Configuration Audit Tool
### Deep-Dive Market Analysis with Web-Validated Data | January 2026

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

## Executive Summary - Revised Valuation

| Stage | Original Doc | First Review | *Final Validated* |
|-------|--------------|--------------|---------------------|
| *Current MVP (Pre-Revenue)* | $75K-$125K | $25K-$45K | *$50,000 - $85,000* |
| *Post-Traction ($50K-$100K ARR)* | $150K-$350K | $125K-$250K | *$200,000 - $450,000* |
| *Exit Ceiling ($300K+ ARR)* | N/A | $400K-$800K | *$1M - $4M* |

### Why the Upward Revision?

Initial analysis undervalued two enterprise-critical features:
1. *SIEM Integration* with real-time alerting (+$15K-$25K)
2. *RAG-powered AI Chat* with NIST SP 800-53 knowledge base (+$10K-$15K)

These aren't "nice-to-haves" — they're enterprise requirements and competitive differentiators.`;

    try {
      await navigator.clipboard.writeText(mvpContent);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      const textArea = document.createElement('textarea');
      textArea.value = mvpContent;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const blob = await nistService.downloadMarketValidationReport();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NSCAT_Market_Validation_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setCopySuccess(false);
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/mvp-overview')}
        sx={{ mb: 2, textTransform: 'none', fontWeight: 500 }}
      >
        Back to Knowledge Base
      </Button>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#1e293b', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <BusinessIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={600}>
                NSCAT Market Validation Report
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Network Security Configuration Audit Tool - Deep-Dive Market Analysis with Web-Validated Data | January 2026
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Refresh MVP Data">
              <IconButton
                onClick={handleRefreshMvp}
                disabled={mvpRefreshing}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  },
                  '&:disabled': {
                    color: 'rgba(255, 255, 255, 0.5)'
                  }
                }}
              >
                {mvpRefreshing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy MVP Information">
              <IconButton
                onClick={handleCopyMvpInfo}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download Market Validation Report PDF">
              <IconButton
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  },
                  '&:disabled': {
                    color: 'rgba(255, 255, 255, 0.5)'
                  }
                }}
              >
                {downloadingPdf ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <GetAppIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Executive Summary */}
      <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            Executive Summary - Revised Valuation
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
            NSCAT Market Validation Report (Revised) | Deep-Dive Market Analysis with Web-Validated Data | January 2026
          </Typography>
          <Box sx={{ mt: 2, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Stage</strong></TableCell>
                  <TableCell align="center"><strong>Original Doc</strong></TableCell>
                  <TableCell align="center"><strong>First Review</strong></TableCell>
                  <TableCell align="center" sx={{ bgcolor: 'success.100' }}><strong>Final Validated</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><strong>Current MVP (Pre-Revenue)</strong></TableCell>
                  <TableCell align="center">$75K-$125K</TableCell>
                  <TableCell align="center">$25K-$45K</TableCell>
                  <TableCell align="center" sx={{ bgcolor: 'success.50', fontWeight: 600 }}>$50,000 - $85,000</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Post-Traction ($50K-$100K ARR)</strong></TableCell>
                  <TableCell align="center">$150K-$350K</TableCell>
                  <TableCell align="center">$125K-$250K</TableCell>
                  <TableCell align="center" sx={{ bgcolor: 'success.50', fontWeight: 600 }}>$200,000 - $450,000</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Exit Ceiling ($300K+ ARR)</strong></TableCell>
                  <TableCell align="center">N/A</TableCell>
                  <TableCell align="center">$400K-$800K</TableCell>
                  <TableCell align="center" sx={{ bgcolor: 'success.50', fontWeight: 600 }}>$1M - $4M</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Why the Upward Revision?</strong> Initial analysis undervalued two enterprise-critical features:
              <br />1. <strong>SIEM Integration</strong> with real-time alerting (+$15K-$25K)
              <br />2. <strong>RAG-powered AI Chat</strong> with NIST SP 800-53 knowledge base (+$10K-$15K)
              <br /><br />
              These aren't "nice-to-haves" — they're enterprise requirements and competitive differentiators.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Product Assessment */}
      <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'primary.main' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            Product Assessment
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            NSCAT (Network Security Compliance Audit Tool) is an AI-powered enterprise firewall security auditing platform that automates compliance assessments across multi-vendor firewall environments.
          </Typography>
          <Typography variant="subtitle2" fontWeight={600} color="success.main" gutterBottom sx={{ mt: 2 }}>
            ✅ STRENGTHS OBSERVED
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Functional Multi-Vendor Support"
                secondary="6 firewall types configured (Checkpoint, Cisco FTD, Fortinet, Huawei USG, Juniper vSRX, PaloAlto) | 535+ audits completed"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Comprehensive AI Integration"
                secondary="18 Local LLMs (Ollama) + 3 Paid providers (OpenAI, Google AI, Groq, OpenRouter)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="NIST SP 800-53 Rev 5 Standards Coverage"
                secondary="31 sections across 4 categories fully documented | RAG-based compliance mapping"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Working Features"
                secondary="PDF reports, SIEM integration, audit history, configuration storage, professional UI/UX"
              />
            </ListItem>
          </List>
          <Typography variant="subtitle2" fontWeight={600} color="warning.main" gutterBottom sx={{ mt: 2 }}>
            ⚠️ CRITICAL WEAKNESSES
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><InfoIcon color="warning" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="MVP/Demo Stage Product"
                secondary="Appears to be functional demo with test data rather than production-ready software"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><InfoIcon color="warning" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Limited Market Validation"
                secondary="No visible customers, testimonials, or case studies | No pricing/commercial structure"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><InfoIcon color="warning" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Competitive Market Reality"
                secondary="Established players: Tufin ($100M+), FireMon ($50M+), AlgoSec with 20+ years market presence"
              />
            </ListItem>
          </List>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                <ComputerIcon color="primary" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>Multi-Vendor Support</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Palo Alto, Fortinet, Checkpoint, Cisco FTD, Juniper SRX, Huawei USG
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                <SpeedIcon color="primary" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>AI-Powered Analysis</Typography>
                  <Typography variant="caption" color="text.secondary">
                    OpenAI, Google AI, Groq, Ollama, OpenRouter with RAG semantic search
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                <PolicyIcon color="primary" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>Compliance Mapping</Typography>
                  <Typography variant="caption" color="text.secondary">
                    NIST SP 800-53 Rev 5 with RAG-based automated scoring
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                <AssessmentIcon color="primary" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>PDF Reports</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Automated audit reports with executive summaries
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                <CloudSyncIcon color="primary" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>SOC/SIEM Integration</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Real-time syslog forwarding, severity-based alerts (Critical/High/Medium), 535+ audits forwarded successfully. Market Value: +$15K-$25K
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                <ChatIcon color="primary" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>AI Chat with RAG</Typography>
                  <Typography variant="caption" color="text.secondary">
                    2 specialized modes (General, NIST SP 800-53), 1,014 NIST controls indexed in ChromaDB, 21 AI models. Market Value: +$10K-$15K
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Critical Features - SIEM & AI Chat */}
      <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'error.main' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="error" />
            Critical Features I Undervalued
          </Typography>
          
          {/* SIEM Integration */}
          <Box sx={{ mt: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} color="error.main" gutterBottom>
              1. SIEM INTEGRATION - SIGNIFICANT VALUE ADD (+$15K-$25K)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>What I observed:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="error" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Automated Alert System"
                  secondary="Compliance scores trigger severity-based alerts: Critical (0-20%), High (21-40%), Medium (41-70%), Fully Compliant (71-100%)"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="error" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Real-time Syslog Forwarding"
                  secondary="UDP/TCP to external SIEM platforms (configured to 192.168.100.211:514)"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="error" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Success Tracking"
                  secondary="All 535+ audits successfully forwarded with 'success' status"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="error" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Filtering & History"
                  secondary="Date range, firewall IP, alert type, min/max score filters"
                />
              </ListItem>
            </List>
            <Alert severity="success" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Why This Matters:</strong> SOC Integration enables security operations centers to monitor firewall compliance in real-time. 
                Critical alerts can trigger automated workflows in Splunk, QRadar, LogRhythm, etc. 
                Most large organizations won't adopt security tools without SIEM integration. 
                Comparable SIEM integrations from competitors cost $10K-$30K/year per enterprise customer.
              </Typography>
            </Alert>
          </Box>

          {/* AI Chat with RAG */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} color="error.main" gutterBottom>
              2. AI CHAT WITH SPECIALIZED MODES - UNDERESTIMATED (+$10K-$15K)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>What I Discovered:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="error" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Two Specialized Modes"
                  secondary="General (Q&A) and NIST SP 800-53 (RAG + LLM hybrid using 1,014 controls)"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="error" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="RAG Implementation"
                  secondary="Retrieval-Augmented Generation using ChromaDB with 1,014 NIST SP 800-53 controls indexed"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="error" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Multi-LLM Support"
                  secondary="21 AI models (18 local + 3 paid) means no vendor lock-in and cost flexibility"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="error" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Contextual Help"
                  secondary="NIST SP 800-53 mode provides standards-based compliance guidance via RAG"
                />
              </ListItem>
            </List>
            <Alert severity="success" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Why This Is Valuable:</strong> AI-powered security advisors are emerging as high-value features. 
                RAG-based compliance Q&A is a unique competitive advantage. 
                Reduces need for external security consultants ($150-$300/hr).
              </Typography>
            </Alert>
          </Box>
        </CardContent>
      </Card>

      {/* Market Value */}
      <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'success.main' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoneyIcon color="success" />
            Realistic Market Valuation
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.main' }}>
                  <Typography variant="subtitle2" fontWeight={600} color="success.main" gutterBottom>
                    Current MVP State
                  </Typography>
                  <Chip 
                    label="$50K - $85K" 
                    color="success" 
                    sx={{ fontSize: '1.1rem', py: 2, px: 1, fontWeight: 600 }}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Pre-revenue valuation (includes SIEM + AI Chat)
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.main' }}>
                  <Typography variant="subtitle2" fontWeight={600} color="info.main" gutterBottom>
                    Post-Revenue Potential
                  </Typography>
                  <Chip 
                    label="$200K - $450K" 
                    color="info" 
                    sx={{ fontSize: '1.1rem', py: 2, px: 1, fontWeight: 600 }}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    With proven traction & $50K-$150K ARR
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              <strong>Valuation Breakdown:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="Core Development: $35K-$50K (6-9 months, multi-vendor parsers, AI audit engine, frontend, backend)" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="SIEM Integration: $8K-$15K (real-time alerts, syslog forwarding, severity mapping) - Market Value: +$15K-$25K" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="AI Chat with RAG: $7K-$12K (ChromaDB RAG, 2 specialized modes, 21 LLMs, 1,014 NIST controls) - Market Value: +$10K-$15K" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="NIST SP 800-53 Compliance Framework: $5K-$8K (20 control families, 1,014 controls, RAG semantic mapping)" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="Total Development Value: $55K-$85K | Risk Adjustment: -10% | Realistic Market Value: $50K-$85K" />
              </ListItem>
            </List>
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Key Differentiators:</strong> SIEM integration makes this enterprise-viable. AI Chat with RAG-powered NIST SP 800-53 knowledge is a genuine innovation competitors don't have. 535+ completed audits prove functionality.
              </Typography>
            </Alert>
          </Box>
        </CardContent>
      </Card>

      {/* Market Potential */}
      <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'warning.main' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon color="warning" />
            Market Potential
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Target Market Size: $1.5B - $2B (Firewall Management & Compliance)</strong>
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: '#0f172a' }}>
                <Typography variant="subtitle2" fontWeight={600}>Mid-Market</Typography>
                <Typography variant="h6" color="primary">$350M</Typography>
                <Typography variant="caption" color="text.secondary">
                  500-5K employees, $8K-$35K/year
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: '#0f172a' }}>
                <Typography variant="subtitle2" fontWeight={600}>Enterprise</Typography>
                <Typography variant="h6" color="primary">$800M</Typography>
                <Typography variant="caption" color="text.secondary">
                  5K+ employees, $40K-$300K/year
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: '#0f172a' }}>
                <Typography variant="subtitle2" fontWeight={600}>MSSPs</Typography>
                <Typography variant="h6" color="primary">$350M</Typography>
                <Typography variant="caption" color="text.secondary">
                  Service revenue multiplier: 2-4x
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Realistic Revenue Projections:</Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="info" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Year 1-2: $25K-$120K ARR"
                  secondary="5-12 customers @ $5K-$10K/year | SMB: $5K-$7K (3-5), Mid-Market: $8K-$12K (2-5), MSSP: $10K-$15K (1-2)"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="info" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Year 3-5: $300K-$1.5M ARR"
                  secondary="30-75 customers @ $10K-$20K/year | Expansion: More vendors, PCI-DSS/HIPAA frameworks, analytics upsells"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="info" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Exit Potential: $1M - $4M"
                  secondary="Strategic acquisition at $300K-$1M ARR | Multiple: 3-4x ARR (AI-enhanced security tools) | Timing: 3-5 years"
                />
              </ListItem>
            </List>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Market Reality:</strong> Enterprise security sales cycles are 6-18 months with high CAC. 
                First 10 customers typically take 12-24 months for B2B security tools. SIEM integration and AI Chat differentiate you from basic audit tools.
              </Typography>
            </Alert>
          </Box>
        </CardContent>
      </Card>

      {/* Competitive Advantages */}
      <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'info.main' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="info" />
            Competitive Positioning
          </Typography>
          
          {/* Competitive Table */}
          <Box sx={{ mt: 2, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Feature</strong></TableCell>
                  <TableCell align="center"><strong>Tufin</strong></TableCell>
                  <TableCell align="center"><strong>FireMon</strong></TableCell>
                  <TableCell align="center"><strong>AlgoSec</strong></TableCell>
                  <TableCell align="center"><strong>NSCAT</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Multi-Vendor</TableCell>
                  <TableCell align="center">✅</TableCell>
                  <TableCell align="center">✅</TableCell>
                  <TableCell align="center">✅</TableCell>
                  <TableCell align="center">✅</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>AI-Powered Audit</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">Limited</TableCell>
                  <TableCell align="center">✅ (RAG + 21 models)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>SIEM Integration</TableCell>
                  <TableCell align="center">✅</TableCell>
                  <TableCell align="center">✅</TableCell>
                  <TableCell align="center">✅</TableCell>
                  <TableCell align="center">✅</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>AI Chat Assistant</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">✅ (Unique)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>RAG with Benchmarks</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">✅ (Unique)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Price Point</TableCell>
                  <TableCell align="center">$50K-$200K</TableCell>
                  <TableCell align="center">$40K-$150K</TableCell>
                  <TableCell align="center">$45K-$180K</TableCell>
                  <TableCell align="center"><strong>Target: $8K-$25K</strong></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Local LLM Support</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">✅ (Cost advantage)</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
            Your Differentiation:
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>AI-First Architecture</Typography>
                <Typography variant="caption" color="text.secondary">
                  Only solution with RAG-powered compliance assistant. 21 model options vs. single proprietary engine.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Cost-Effective AI</Typography>
                <Typography variant="caption" color="text.secondary">
                  18 local LLMs = no ongoing AI API costs for cost-conscious customers. Flexible AI model selection.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Modern UX</Typography>
                <Typography variant="caption" color="text.secondary">
                  Clean, intuitive interface vs. legacy enterprise UI. Built for modern security teams.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Multi-Vendor Native</Typography>
                <Typography variant="caption" color="text.secondary">
                  Built from ground-up for heterogeneous environments, unified interface, single compliance framework.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Technical Stack */}
      <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'secondary.main' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ArchitectureIcon color="secondary" />
            Technical Stack
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {['FastAPI (Python)', 'PostgreSQL', 'ChromaDB (RAG)', 'React.js', 'Material-UI', 'OpenAI API', 'Google AI', 'Groq', 'Ollama', 'OpenRouter'].map((tech) => (
              <Chip key={tech} label={tech} variant="outlined" size="small" />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Timeline: MVP to Market */}
      <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'info.main' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon color="info" />
            Timeline: MVP to Market
          </Typography>
          <Box sx={{ mt: 3, position: 'relative' }}>
            {/* Timeline Line */}
            <Box
              sx={{
                position: 'absolute',
                left: '24px',
                top: 0,
                bottom: 0,
                width: '2px',
                backgroundColor: 'primary.main',
                zIndex: 0
              }}
            />
            
            {/* Phase 1: Current MVP Status */}
            <Box sx={{ display: 'flex', alignItems: 'start', mb: 4, position: 'relative', zIndex: 1 }}>
              <Box sx={{ mr: 2, mt: 0.5 }}>
                <CheckCircleOutlineIcon color="success" sx={{ fontSize: 32 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} color="success.main" gutterBottom>
                  Phase 1: MVP Complete ✅
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  <strong>Current Status:</strong> 6 firewall vendors integrated
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {['Palo Alto Networks', 'Fortinet FortiGate', 'Checkpoint', 'Cisco FTD', 'Juniper SRX', 'Huawei USG'].map((vendor) => (
                    <Chip key={vendor} label={vendor} size="small" color="success" variant="outlined" />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  ✅ Core features operational: AI-powered auditing, NIST SP 800-53 compliance, PDF reports, multi-user support
                </Typography>
              </Box>
            </Box>

            {/* Phase 2: Device Integration */}
            <Box sx={{ display: 'flex', alignItems: 'start', mb: 4, position: 'relative', zIndex: 1 }}>
              <Box sx={{ mr: 2, mt: 0.5 }}>
                <RadioButtonCheckedIcon color="warning" sx={{ fontSize: 32 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} color="warning.main" gutterBottom>
                  Phase 2: Device Integration (In Progress) 🔄
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  <strong>Target:</strong> Integrate 10 additional devices from top 3 vendors
                </Typography>
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 1.5, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.main' }}>
                      <Typography variant="subtitle2" fontWeight={600} color="warning.main">Palo Alto</Typography>
                      <Typography variant="body2" color="text.secondary">4-5 device models</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 1.5, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.main' }}>
                      <Typography variant="subtitle2" fontWeight={600} color="warning.main">Fortinet</Typography>
                      <Typography variant="body2" color="text.secondary">3-4 device models</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 1.5, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.main' }}>
                      <Typography variant="subtitle2" fontWeight={600} color="warning.main">Checkpoint</Typography>
                      <Typography variant="body2" color="text.secondary">2-3 device models</Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary">
                  ⏱️ Estimated: 3-6 months | Focus: Expand device model coverage for enterprise customers | Reality: Critical for market readiness
                </Typography>
              </Box>
            </Box>

            {/* Phase 3: Market Readiness */}
            <Box sx={{ display: 'flex', alignItems: 'start', mb: 4, position: 'relative', zIndex: 1 }}>
              <Box sx={{ mr: 2, mt: 0.5 }}>
                <RadioButtonUncheckedIcon color="primary" sx={{ fontSize: 32 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} color="primary.main" gutterBottom>
                  Phase 3: Market Readiness (Q3-Q4 2025)
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="info" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Enterprise Features"
                      secondary="SOC 2 Type 1, Advanced RBAC, SSO (SAML/OAuth), API integrations"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="info" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Customer Validation"
                      secondary="3-5 pilot programs, case studies with ROI metrics, testimonials"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="info" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Go-to-Market"
                      secondary="Channel partner program, marketing website, pricing tiers"
                    />
                  </ListItem>
                </List>
                <Typography variant="caption" color="text.secondary">
                  ⏱️ Estimated: 6-9 months | Target: First 3-5 paying customers ($15K-$40K ARR) | Requirements: SOC2 Type 1, SSO, RBAC, case studies
                </Typography>
              </Box>
            </Box>

            {/* Phase 4: Market Launch */}
            <Box sx={{ display: 'flex', alignItems: 'start', position: 'relative', zIndex: 1 }}>
              <Box sx={{ mr: 2, mt: 0.5 }}>
                <RadioButtonUncheckedIcon sx={{ fontSize: 32, color: 'grey.400' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.secondary" gutterBottom>
                  Phase 4: Market Launch (Q1-Q2 2026)
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon sx={{ color: 'grey.400' }} fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Revenue Generation (Year 1-2)"
                      secondary="5-10 customers, $15K-$80K ARR (12-24 months to acquire first 10 customers)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon sx={{ color: 'grey.400' }} fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Market Expansion (Year 3-5)"
                      secondary="25-50 customers, $200K-$1M ARR | Additional vendors, compliance frameworks"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon sx={{ color: 'grey.400' }} fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Scale Operations"
                      secondary="MSSP partnerships, enterprise sales team, customer success | 80%+ retention required"
                    />
                  </ListItem>
                </List>
                <Typography variant="caption" color="text.secondary">
                  ⏱️ Target: $200K-$1M ARR by Year 3-5 | Requires: 80%+ retention, channel partnerships, enterprise features
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Path to Market */}
      <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'secondary.main' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon color="secondary" />
            Path to $1M+ Valuation
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'secondary.50', border: '1px solid', borderColor: 'secondary.main' }}>
                <Typography variant="subtitle2" fontWeight={600} color="secondary.main" gutterBottom>
                  6-Month Milestones
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="3-5 paying customers ($15K-$40K ARR)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="3 detailed case studies with ROI metrics" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Enterprise features: SSO, RBAC, audit logs" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="SOC2 Type 1 certification" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'secondary.50', border: '1px solid', borderColor: 'secondary.main' }}>
                <Typography variant="subtitle2" fontWeight={600} color="secondary.main" gutterBottom>
                  12-Month Milestones
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="$100K ARR (10-15 customers)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="80%+ retention rate demonstrated" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="SIEM integrations (Splunk, QRadar)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Channel partner strategy launched" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Pakistan Market Analysis */}
      <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'success.main' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoneyIcon color="success" />
            Pakistan Market Analysis
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.main' }}>
                <Typography variant="subtitle2" fontWeight={600} color="success.main" gutterBottom>
                  Realistic Market Value
                </Typography>
                <Chip 
                  label="PKR 10M - PKR 30M" 
                  color="success" 
                  sx={{ fontSize: '1.1rem', py: 2, px: 1, fontWeight: 600 }}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  ($36K - $108K USD)
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.main' }}>
                <Typography variant="subtitle2" fontWeight={600} color="info.main" gutterBottom>
                  Pricing per Organization
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>SMB:</strong> PKR 150K - PKR 300K/year<br />
                  <strong>Mid-Market:</strong> PKR 500K - PKR 1M/year<br />
                  <strong>Enterprise:</strong> PKR 1.5M - PKR 3M/year<br />
                  <strong>Government:</strong> PKR 1.4M - PKR 3.5M/year
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Net Earnings After Expenses</Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><InfoIcon color="error" fontSize="small" /></ListItemIcon>
                <ListItemText primary="Year 1-2: Loss (PKR -4M to -6M)" />
              </ListItem>
              <ListItem>
                <ListItemIcon><InfoIcon color="warning" fontSize="small" /></ListItemIcon>
                <ListItemText primary="Year 3: Near break-even (PKR -1M to +500K)" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="Year 4: PKR 1M - PKR 2M profit" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                <ListItemText primary="Year 5: PKR 3M - PKR 5M profit" />
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Key Insights:</strong> Market size: $60.89M by 2025, growing to $95M by 2030. 
                Government IT allocation: Rs. 23 billion (FY 2024-25). Break-even: Year 3-4 with 10-15 customers. 
                Required capital: PKR 5M - PKR 10M for 18-24 months.
              </Typography>
            </Alert>
          </Box>
        </CardContent>
      </Card>

      {/* Investment Recommendation */}
      <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'error.main', bgcolor: 'error.50' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Investment Recommendation
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Current Reality:</strong> Well-built MVP worth $50K-$85K based on development effort, SIEM integration, and AI Chat with RAG. 
            With SIEM integration and AI chat with RAG, and 535+ working audits across 6 vendors, your product is enterprise-viable.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Path Forward:</strong> Focus on landing 5-10 paying customers to validate product-market fit and justify a $200K-$450K valuation. 
            The SIEM + AI Chat features make you more competitive than initially assessed.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>For Bootstrapping:</strong> Current value $50K-$85K is fair. Land first 5 customers organically ($25K-$50K ARR), reinvest in sales/marketing.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>For Angel Investment:</strong> Seeking $200K-$400K at $1.2M-$2M post-money (realistic for AI-enhanced security SaaS). 
            Use of funds: Sales team ($80K-$120K), Marketing & partnerships ($40K-$60K), SOC2 Type 2 ($20K-$30K), 
            Product enhancements ($30K-$50K). 18-24 months runway.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>For Strategic Sale:</strong> Best timing at $300K-$500K ARR with 70%+ retention. 
            Likely buyers: Mid-tier security vendors (Rapid7, Qualys, Tenable), SIEM platforms needing firewall compliance modules, MSSPs. 
            Expected price: $1M-$3M.
          </Typography>
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Bottom Line:</strong> I was wrong about $35K-$60K. With SIEM integration, AI chat with RAG, and 535+ working audits, 
              your product is worth $50K-$85K today. The SIEM integration alone makes this enterprise-viable, and the AI chat with RAG-powered 
              NIST SP 800-53 knowledge is a genuine innovation that competitors don't have.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Funding Options - Summary */}
      <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'warning.main', bgcolor: 'warning.50' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoneyIcon color="warning" />
            NSCAT Funding Options Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
            Pre-Revenue Cybersecurity Startup | January 2026
          </Typography>

          {/* Executive Summary */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Executive Summary</Typography>
            <Box sx={{ overflowX: 'auto', mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Current State</strong></TableCell>
                    <TableCell><strong>Funding Reality</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Revenue</strong></TableCell>
                    <TableCell>$0</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Customers</strong></TableCell>
                    <TableCell>0</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>MVP Status</strong></TableCell>
                    <TableCell>Complete</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Realistic Funding Now</strong></TableCell>
                    <TableCell>$50,000 - $150,000</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Realistic Valuation</strong></TableCell>
                    <TableCell>$500K - $1.5M cap</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Recommendation</strong></TableCell>
                    <TableCell><strong>Get customers first, then raise</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Box>

          {/* Funding Options Summary */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Funding Options Summary</Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'success.50', height: '100%' }}>
                  <Typography variant="subtitle2" fontWeight={600} color="success.main" gutterBottom>Friends & Family</Typography>
                  <Typography variant="h6">$10K-$50K</Typography>
                  <Chip label="50-70%" color="success" size="small" sx={{ mt: 1 }} />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Most realistic for current stage
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'warning.50', height: '100%' }}>
                  <Typography variant="subtitle2" fontWeight={600} color="warning.main" gutterBottom>Accelerators</Typography>
                  <Typography variant="h6">$100K-$500K</Typography>
                  <Chip label="15-25%" color="warning" size="small" sx={{ mt: 1 }} />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Best path for $0 revenue (MACH37 recommended)
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'info.50', height: '100%' }}>
                  <Typography variant="subtitle2" fontWeight={600} color="info.main" gutterBottom>SBIR Grants</Typography>
                  <Typography variant="h6">$50K-$275K</Typography>
                  <Chip label="0% equity" color="success" size="small" sx={{ mt: 1 }} />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Non-dilutive capital (DHS, NSA, NIST)
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Final Recommendation:</strong> Revenue is the best fundraising. 
              5 paying customers at $5K-$10K/year ($25K-$50K ARR) would let you raise $200K-$400K at a $1.5M-$2.5M valuation — 
              <strong>3x better terms than today.</strong> Spend the next 3 months on customer acquisition, not investor pitches.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="Action completed successfully!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default MarketValidation;
